import { Link } from 'react-router-dom'
import {
  LayoutDashboard,
  ListTodo,
  FileText,
  Package,
  Gauge,
  Building2,
  Settings,
  ChevronLeft,
  ChevronRight,
  Receipt,
  ArrowRightLeft,
  HandCoins,
  CreditCard,
  BookOpen,
  UserCheck,
  BarChart3,
  Globe,
  Settings2,
  Link2,
  HelpCircle,
  Home,
  TrendingUp,
  CalendarDays,
  ClipboardList,
  MessageCircle,
  Handshake,
FileSignature } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useUIStore } from '@/stores/ui-store'
import { useAuthStore } from '@/stores/auth-store'
import { useHasPermission } from '@/hooks/useHasPermission'
import { Permissions } from '@/lib/permissions'
import { useSidebarBadges } from '@/hooks/useSidebarBadges'
import { isCompanyHead } from '@/features/sales/constants'
import { useHotLeadBadge } from '@/features/sales/hooks/useHotLeadBadge'
import { useScoreboardAccess } from '@/features/sales/scoreboard/useScoreboard'
import { NavItem, NavSectionHeader, type NavItemConfig } from './NavItem'
import { useOperationsMenuAccess, visibleMasterDataItems, visibleReportItems } from './operationsMenu'

/**
 * Navigation configuration per App-Shell.md Section 2
 *
 * Structure:
 * - Dashboard (standalone)
 * - Quotations (expandable with sub-items)
 * - Job Orders (expandable with "Soon" badge)
 * - Master Data section (Customers, Providers, Carriers)
 * - Settings (footer)
 */

// Main navigation items (static - Dashboard only)
// Tasks nav item is created dynamically in Sidebar component with badge
const dashboardNav: NavItemConfig = {
  label: 'Dashboard',
  href: '/',
  icon: LayoutDashboard,
}

// Operations items
const quotationsNav: NavItemConfig = {
  label: 'Quotations',
  href: '/quotations',
  icon: FileText,
}

const jobOrdersNav: NavItemConfig = {
  label: 'Job Orders',
  href: '/job-orders',
  icon: Package,
}

// WhatsApp Inbox (Phase 3) — every connected number's conversations, two-pane.
const whatsappNav: NavItemConfig = {
  label: 'WhatsApp',
  href: '/whatsapp',
  icon: MessageCircle,
}

// Action Items — internal team task management. Mounted per workspace (like
// My Follow-ups) because AppLayout derives the workspace from the URL prefix;
// each workspace's nav points at its own mount so the sidebar stays put.
// Calendar (schedule module, phase 10). Rendered TWICE below — the collapsed rail and the
// mobile sheet are separate trees — so this is two edits, not one. Gated on
// `calendar-event:view`: without it the entry does not appear at all, rather than appearing and
// 403-ing, because a nav item that always fails is worse than an absent one.
//
// **ONE nav entry, not two.** To-do is a TAB on this page, not a sibling of it: the queue and the
// grid are two views of one projection, and giving each its own sidebar row taught people they
// were two places with two datasets. The route keeps the word `schedule` — the label is what the
// product calls it, and renaming the path would break every saved link and the two `follow-ups`
// redirects that already point at it.
const scheduleNav: NavItemConfig = {
  label: 'Calendar',
  href: '/schedule',
  icon: CalendarDays,
}

const actionItemsNav: NavItemConfig = {
  label: 'Action Items',
  href: '/action-items',
  icon: ClipboardList,
}

const financeActionItemsNav: NavItemConfig = {
  label: 'Action Items',
  href: '/finance/action-items',
  icon: ClipboardList,
}

// Sales items (v1 Lead Management)
// Sales-specific admin lives inside the master Settings page (Sales tab),
// not as a sales workspace nav entry, so users find all tenant config in one place.
// Customers reuses the operations CustomerListPage (same component, sales route).
/**
 * Sales nav — **and every entry states the permission it needs, because this list is now read
 * by two different kinds of reader.**
 *
 * A nav item that always dead-ends on Unauthorized is worse than an absent one, so each flag
 * below mirrors the `ModuleGate` on its own route in `Router.tsx`:
 *
 * | flag | route gate |
 * |---|---|
 * | `requiresSales` | `sales:access` — the Sales seat proper |
 * | `requiresCalendar` | `calendar-event:view` |
 * | `requiresOnboarding` | `onboarding-case:view` |
 *
 * **Onboarding is the one entry an approver can reach without a Sales seat**, and that is the
 * whole reason `requiresSales` had to exist. Finance Officer, Operations Manager, Accounts
 * Manager and MANAGER hold `onboarding-case:view` and `onboarding-case:approve` and **no**
 * `sales:access` (`SystemRoleSeedData`, confirmed against testco). The entry used to be
 * duplicated into the Operations and Finance sidebars for exactly that reason — Madhav,
 * 2026-09-11: *"I want the onbaording tab removed from the finance and oeprations tab, the ops
 * and finacn people will have access to the onboarding tab in sales"*.
 *
 * So it is declared **once**, here, and an approver gets to it by switching to the Sales
 * workspace — which `WorkspaceSwitcher` now offers them on `onboarding-case:view`, and
 * `AppLayout` now honours on the `/sales` prefix. In that workspace this list filters down to
 * Calendar and Onboarding; the four `requiresSales` entries are seats they do not hold.
 */
type SalesNavItem = NavItemConfig & {
  requiresSales?: boolean
  requiresCalendar?: boolean
  requiresOnboarding?: boolean
}

const salesItems: SalesNavItem[] = [
  {
    label: 'My Day',
    href: '/sales/home',
    icon: Home,
    requiresSales: true,
  },
  {
    label: 'Calendar',
    href: '/sales/schedule',
    icon: CalendarDays,
    requiresCalendar: true,
  },
  {
    label: 'Action Items',
    href: '/sales/action-items',
    icon: ClipboardList,
    requiresSales: true,
  },
  {
    label: 'Pipeline',
    href: '/sales/leads',
    icon: TrendingUp,
    requiresSales: true,
  },
  {
    label: 'Lead Pool',
    href: '/sales/pool',
    icon: Handshake,
    requiresSales: true,
  },
  {
    label: 'Onboarding',
    href: '/sales/onboarding',
    icon: FileSignature,
    requiresOnboarding: true,
  },
]

/** The Lead Pool entry, carrying the unclaimed-HOT badge when there is one (dispatch L6). */
function withPoolBadge(item: SalesNavItem, poolBadge: number | undefined): SalesNavItem {
  return item.href === '/sales/pool' && poolBadge !== undefined ? { ...item, badge: poolBadge } : item
}

/** The one predicate both render trees use, so the two sidebars cannot drift apart. */
function navVisible(
  item: SalesNavItem,
  can: { sales: boolean; calendar: boolean; onboarding: boolean },
): boolean {
  if (item.requiresSales && !can.sales) return false
  if (item.requiresCalendar && !can.calendar) return false
  if (item.requiresOnboarding && !can.onboarding) return false
  return true
}

/**
 * Sales scoreboard (P2.1): a leader of a tenant with the scoreboard on gets "Team" first — their
 * home — and My Day keeps their own day reachable (`?mine=1` stops the leader landing redirect).
 * Anyone else, and every tenant without the scoreboard, gets the list exactly as before.
 */
const teamNav: SalesNavItem = { label: 'Team', href: '/sales/team', icon: Gauge, requiresSales: true }

function withScoreboard(items: SalesNavItem[], scoreboardLeader: boolean): SalesNavItem[] {
  if (!scoreboardLeader) return items
  return [teamNav, ...items.map((i) => (i.href === '/sales/home' ? { ...i, href: '/sales/home?mine=1' } : i))]
}


// Finance navigation - Grouped & Hierarchical
// Main Menu - takes user back to Finance landing page
const financeMainMenuNav: NavItemConfig = {
  label: 'Main Menu',
  href: '/finance',
  icon: Home,
}

// Daily Overview (full operational dashboard)
const financeDashboardNav: NavItemConfig = {
  label: 'Daily Overview',
  href: '/finance/dashboard',
  icon: LayoutDashboard,
}

// Daily Work section
const financeTransactionsNav: NavItemConfig = {
  label: 'All Transactions',
  icon: ArrowRightLeft,
  href: '/finance/transactions',
}

const financeAllocationsNav: NavItemConfig = {
  label: 'Allocations',
  icon: Link2,
  href: '/finance/allocations',
}

const financeCollectionsNav: NavItemConfig = {
  label: 'Collections',
  href: '/finance/receivables',
  icon: HandCoins,
}

const financePaymentsDueNav: NavItemConfig = {
  label: 'Payments Due',
  href: '/finance/payables',
  icon: CreditCard,
}

const financeChequeNav: NavItemConfig = {
  label: 'Cheque Tracking',
  href: '/finance/cheques',
  icon: Receipt,
}

// Masters section - expandable
const financeAccountsNav: NavItemConfig = {
  label: 'Accounts',
  icon: BookOpen,
  children: [
    { label: 'Chart of Accounts', href: '/finance/masters/accounts' },
    { label: 'Subledgers', href: '/finance/masters/parties', icon: UserCheck },
  ],
}

// Reports
const financeReportsNav: NavItemConfig = {
  label: 'Reports',
  href: '/finance/reports',
  icon: BarChart3,
}

// Settings
const financeSettingsNav: NavItemConfig = {
  label: 'Finance Config',
  href: '/finance/settings',
  icon: Settings2,
}

// User Guide
const financeUserGuideNav: NavItemConfig = {
  label: 'User Guide',
  href: '/finance/guide',
  icon: HelpCircle,
}

// Footer items
const footerItems: NavItemConfig[] = [
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
  },
]

// The Sales guide sits in the footer beside Settings — it is reference material,
// not daily work, and the contextual help (ⓘ hints, the tour, the "How it works"
// button) already covers the in-flow case on the working screens.
const salesGuideNav: NavItemConfig = {
  label: 'User Guide',
  href: '/sales/guide',
  icon: BookOpen,
}

// Sales keeps its own settings inside the Sales workspace (only sales config),
// so clicking Settings from Sales doesn't bounce the user into Operations.
const salesSettingsNav: NavItemConfig = {
  label: 'Settings',
  href: '/sales/settings',
  icon: Settings,
}

// Sales footer order: Dashboard → Companies → Book of Business (company head only) → Settings.
// Companies is a master/reference list, not a daily pipeline view, so it sits below the Dashboard.
const salesCompaniesNav: NavItemConfig = {
  label: 'Companies',
  href: '/sales/customers',
  icon: Building2,
}

// Dashboard is analytics/review — a manager-grade surface, not a rep's daily driver — so it sits in
// the footer, keeping the rep's primary nav to just the two "do the work" views (My Day, Pipeline).
const salesDashboardNav: NavItemConfig = {
  label: 'Dashboard',
  href: '/sales/dashboard',
  icon: BarChart3,
}

// Website enquiries — the monthly report (Kiko #29), beneath Dashboard.
const salesWebsiteEnquiriesNav: NavItemConfig = {
  label: 'Website enquiries',
  href: '/sales/website-enquiries',
  icon: Globe,
}

// Book of business is the company head's installed-base revenue view — shown only to that one
// person (email-gated), beneath Dashboard.
const salesBookOfBusinessNav: NavItemConfig = {
  label: 'Book of business',
  href: '/sales/book-of-business',
  icon: HandCoins,
}

interface SidebarProps {
  className?: string
}

/**
 * Main sidebar navigation component
 * Per App-Shell.md Section 2 specifications
 *
 * Features:
 * - Collapsible (256px → 72px)
 * - Expandable sections
 * - Active state indicators
 * - Badge counts
 * - Responsive (hidden on mobile, shown via Sheet)
 */
export function Sidebar({ className }: SidebarProps) {
  const {
    sidebarCollapsed,
    toggleSidebar,
    workspace,
    expandedSections,
    toggleSection,
    collapseAllSections,
  } = useUIStore()

  // Book of business is visible only to the sales company head, and only with
  // `report:accounts-revenue` — the same permission as its route and the gateway row (audit S-82).
  const canSeeAccountsRevenue = useHasPermission(Permissions.REPORT_ACCOUNTS_REVENUE)
  const showBookOfBusiness = isCompanyHead(useAuthStore((s) => s.user?.email)) && canSeeAccountsRevenue
  const canSeeSchedule = useHasPermission(Permissions.CALENDAR_EVENT_VIEW)
  const canSeeOnboarding = useHasPermission(Permissions.ONBOARDING_CASE_VIEW)
  // Customers, the three reports and Sales → Companies: each on its own permission.
  const menuAccess = useOperationsMenuAccess()
  const masterData = visibleMasterDataItems(menuAccess)
  const reportItems = visibleReportItems(menuAccess)
  // The Sales seat proper. An approver reaches /sales/onboarding without it, which is why
  // every other entry in `salesItems` carries `requiresSales`.
  const hasSalesAccess = useHasPermission(Permissions.SALES_ACCESS)
  // Unclaimed HOT leads (dispatch L6) — polled only while the Sales nav is on screen.
  const poolBadge = useHotLeadBadge(hasSalesAccess && workspace === 'sales')
  const scoreboard = useScoreboardAccess(hasSalesAccess && workspace === 'sales')
  const scoreboardLeader = scoreboard.access.kind === 'on' && scoreboard.access.isLeader

  // Fetch task counts for badge
  const { taskCount, hasUrgent } = useSidebarBadges()

  // Tasks nav item with dynamic badge
  const tasksNav: NavItemConfig = {
    label: 'Tasks',
    href: '/tasks',
    icon: ListTodo,
    badge: taskCount > 0 ? taskCount : undefined,
    badgeVariant: hasUrgent ? 'destructive' : 'secondary',
  }

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar',
        'transition-all duration-200 ease-in-out',
        sidebarCollapsed ? 'w-[72px]' : 'w-64',
        className
      )}
    >
      {/* Header with logo and collapse toggle */}
      <div
        className={cn(
          'flex h-14 items-center border-b',
          sidebarCollapsed ? 'justify-center px-3' : 'justify-between px-3'
        )}
      >
        <Link
          to={workspace === 'finance' ? '/finance/dashboard' : '/'}
          className={cn(
            'flex items-center gap-2',
            sidebarCollapsed && 'justify-center'
          )}
          aria-label="FreighAI home"
        >
          {sidebarCollapsed ? (
            <img src="/logo-icon-light.png" alt="FreighAI" className="h-8 w-8 rounded-md object-contain" />
          ) : (
            <>
              <img src="/logo-wordmark-dark.png" alt="FreighAI" className="h-7 w-auto object-contain block dark:hidden" />
              <img src="/logo-wordmark-light.png" alt="FreighAI" className="h-7 w-auto object-contain hidden dark:block" />
            </>
          )}
        </Link>

        {!sidebarCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleSidebar}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {workspace === 'sales' ? (
          // SALES WORKSPACE NAVIGATION (v1 Lead Management)
          <>
            {!sidebarCollapsed && (
              <NavSectionHeader label="Sales" isCollapsed={sidebarCollapsed} />
            )}
            {sidebarCollapsed && <Separator className="my-3" />}
            <div className="space-y-1">
              {withScoreboard(salesItems, scoreboardLeader)
                .filter((item) => navVisible(item, { sales: hasSalesAccess, calendar: canSeeSchedule, onboarding: canSeeOnboarding }))
                .map((item) => (
                  <NavItem key={item.label} item={withPoolBadge(item, poolBadge)} isCollapsed={sidebarCollapsed} />
                ))}
            </div>
          </>
        ) : workspace === 'operations' ? (
          // OPERATIONS WORKSPACE NAVIGATION
          <>
            {/* Main nav items */}
            <div className="space-y-1">
              <NavItem
                item={dashboardNav}
                isCollapsed={sidebarCollapsed}
              />
              <NavItem
                item={tasksNav}
                isCollapsed={sidebarCollapsed}
              />
              <NavItem
                item={actionItemsNav}
                isCollapsed={sidebarCollapsed}
              />
              {canSeeSchedule && (
                <NavItem
                  item={scheduleNav}
                  isCollapsed={sidebarCollapsed}
                />
              )}
            </div>

            {/* Operations Section */}
            {!sidebarCollapsed && (
              <NavSectionHeader label="Operations" isCollapsed={sidebarCollapsed} />
            )}
            {sidebarCollapsed && <Separator className="my-3" />}

            <div className="space-y-1">
              <NavItem
                item={quotationsNav}
                isCollapsed={sidebarCollapsed}
              />
              <NavItem
                item={jobOrdersNav}
                isCollapsed={sidebarCollapsed}
              />
              <NavItem
                item={whatsappNav}
                isCollapsed={sidebarCollapsed}
              />
            </div>

            {/* Master Data Section */}
            {!sidebarCollapsed && (
              <NavSectionHeader label="Master Data" isCollapsed={sidebarCollapsed} />
            )}
            {sidebarCollapsed && <Separator className="my-3" />}

            <div className="space-y-1">
              {masterData.map((item) => (
                <NavItem
                  key={item.label}
                  item={item}
                  isCollapsed={sidebarCollapsed}
                />
              ))}
            </div>

            {/* Reports Section — left out entirely when no report is open to this person */}
            {reportItems.length > 0 && (
              <>
                {!sidebarCollapsed && (
                  <NavSectionHeader label="Reports" isCollapsed={sidebarCollapsed} />
                )}
                {sidebarCollapsed && <Separator className="my-3" />}

                <div className="space-y-1">
                  {reportItems.map((item) => (
                    <NavItem key={item.label} item={item} isCollapsed={sidebarCollapsed} />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          // FINANCE WORKSPACE NAVIGATION
          <>
            {/* Finance Start + Daily Overview */}
            <div className="space-y-1">
              <NavItem
                item={financeMainMenuNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
              <NavItem
                item={financeDashboardNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
            </div>

            {/* Daily Work Section */}
            {!sidebarCollapsed && (
              <NavSectionHeader label="Daily Work" isCollapsed={sidebarCollapsed} />
            )}
            {sidebarCollapsed && <Separator className="my-3" />}

            <div className="space-y-1">
              <NavItem
                item={financeTransactionsNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
              <NavItem
                item={financeAllocationsNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
              <NavItem
                item={financeCollectionsNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
              <NavItem
                item={financePaymentsDueNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
              <NavItem
                item={financeChequeNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
              <NavItem
                item={financeActionItemsNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
            </div>

            {/* Masters Section */}
            {!sidebarCollapsed && (
              <NavSectionHeader label="Masters" isCollapsed={sidebarCollapsed} />
            )}
            {sidebarCollapsed && <Separator className="my-3" />}

            <div className="space-y-1">
              <NavItem
                item={financeAccountsNav}
                isCollapsed={sidebarCollapsed}
                isExpanded={expandedSections.financeAccounts}
                onToggle={() => toggleSection('financeAccounts')}
                onNavigate={collapseAllSections}
              />
            </div>

            {/* Reports Section */}
            {!sidebarCollapsed && (
              <NavSectionHeader label="Reports" isCollapsed={sidebarCollapsed} />
            )}
            {sidebarCollapsed && <Separator className="my-3" />}

            <div className="space-y-1">
              <NavItem
                item={financeReportsNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
            </div>
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t px-3 py-4">
        <div className="space-y-1">
          {workspace === 'finance' ? (
            <>
              <NavItem
                item={financeSettingsNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
              <NavItem
                item={financeUserGuideNav}
                isCollapsed={sidebarCollapsed}
                onNavigate={collapseAllSections}
              />
            </>
          ) : workspace === 'sales' ? (
            <>
              <NavItem item={salesDashboardNav} isCollapsed={sidebarCollapsed} />
              <NavItem item={salesWebsiteEnquiriesNav} isCollapsed={sidebarCollapsed} />
              {menuAccess.customerList && <NavItem item={salesCompaniesNav} isCollapsed={sidebarCollapsed} />}
              {showBookOfBusiness && <NavItem item={salesBookOfBusinessNav} isCollapsed={sidebarCollapsed} />}
              <NavItem item={salesGuideNav} isCollapsed={sidebarCollapsed} />
              <NavItem item={salesSettingsNav} isCollapsed={sidebarCollapsed} />
            </>
          ) : (
            footerItems.map((item) => (
              <NavItem
                key={item.label}
                item={item}
                isCollapsed={sidebarCollapsed}
              />
            ))
          )}
        </div>

        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="mt-2 h-8 w-full"
            onClick={toggleSidebar}
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </aside>
  )
}

/**
 * Sidebar content for mobile sheet
 * Same content but always expanded
 */
export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { workspace, expandedSections, toggleSection, collapseAllSections } = useUIStore()

  // Book of business is visible only to the sales company head (mirrors the desktop Sidebar),
  // and only with `report:accounts-revenue` — the same permission as its route and the gateway row.
  // Previously referenced below without being defined here — a latent ReferenceError on the
  // mobile sales sidebar; defining it fixes that.
  const canSeeAccountsRevenue = useHasPermission(Permissions.REPORT_ACCOUNTS_REVENUE)
  const showBookOfBusiness = isCompanyHead(useAuthStore((s) => s.user?.email)) && canSeeAccountsRevenue
  const canSeeSchedule = useHasPermission(Permissions.CALENDAR_EVENT_VIEW)
  const canSeeOnboarding = useHasPermission(Permissions.ONBOARDING_CASE_VIEW)
  // Customers, the three reports and Sales → Companies: each on its own permission.
  const menuAccess = useOperationsMenuAccess()
  const masterData = visibleMasterDataItems(menuAccess)
  const reportItems = visibleReportItems(menuAccess)
  // The Sales seat proper. An approver reaches /sales/onboarding without it, which is why
  // every other entry in `salesItems` carries `requiresSales`.
  const hasSalesAccess = useHasPermission(Permissions.SALES_ACCESS)
  // Unclaimed HOT leads (dispatch L6) — polled only while the Sales nav is on screen.
  const poolBadge = useHotLeadBadge(hasSalesAccess && workspace === 'sales')
  const scoreboard = useScoreboardAccess(hasSalesAccess && workspace === 'sales')
  const scoreboardLeader = scoreboard.access.kind === 'on' && scoreboard.access.isLeader

  // Fetch task counts for badge
  const { taskCount, hasUrgent } = useSidebarBadges()

  // Tasks nav item with dynamic badge
  const tasksNavMobile: NavItemConfig = {
    label: 'Tasks',
    href: '/tasks',
    icon: ListTodo,
    badge: taskCount > 0 ? taskCount : undefined,
    badgeVariant: hasUrgent ? 'destructive' : 'secondary',
  }

  const handleNavClick = () => {
    collapseAllSections()
    onNavigate?.()
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header with logo */}
      <div className="flex h-14 items-center border-b px-4">
        <Link
          to={workspace === 'finance' ? '/finance/dashboard' : '/'}
          className="flex items-center gap-2"
          onClick={handleNavClick}
          aria-label="FreighAI home"
        >
          <img src="/logo-wordmark-dark.png" alt="FreighAI" className="h-7 w-auto object-contain block dark:hidden" />
          <img src="/logo-wordmark-light.png" alt="FreighAI" className="h-7 w-auto object-contain hidden dark:block" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4" onClick={handleNavClick}>
        {workspace === 'sales' ? (
          // SALES WORKSPACE (v1 Lead Management)
          <>
            <NavSectionHeader label="Sales" isCollapsed={false} />
            <div className="space-y-1">
              {withScoreboard(salesItems, scoreboardLeader)
                .filter((item) => navVisible(item, { sales: hasSalesAccess, calendar: canSeeSchedule, onboarding: canSeeOnboarding }))
                .map((item) => (
                  <NavItem key={item.label} item={withPoolBadge(item, poolBadge)} isCollapsed={false} />
                ))}
            </div>
          </>
        ) : workspace === 'operations' ? (
          // OPERATIONS WORKSPACE
          <>
            {/* Main nav items */}
            <div className="space-y-1">
              <NavItem item={dashboardNav} isCollapsed={false} />
              <NavItem item={tasksNavMobile} isCollapsed={false} />
              <NavItem item={actionItemsNav} isCollapsed={false} />
              {canSeeSchedule && <NavItem item={scheduleNav} isCollapsed={false} />}
            </div>

            {/* Operations Section */}
            <NavSectionHeader label="Operations" isCollapsed={false} />

            <div className="space-y-1">
              <NavItem item={quotationsNav} isCollapsed={false} />
              <NavItem item={jobOrdersNav} isCollapsed={false} />
              <NavItem item={whatsappNav} isCollapsed={false} />
            </div>

            {/* Master Data Section */}
            <NavSectionHeader label="Master Data" isCollapsed={false} />

            <div className="space-y-1">
              {masterData.map((item) => (
                <NavItem key={item.label} item={item} isCollapsed={false} />
              ))}
            </div>

            {/* Reports Section — left out entirely when no report is open to this person */}
            {reportItems.length > 0 && (
              <>
                <NavSectionHeader label="Reports" isCollapsed={false} />

                <div className="space-y-1">
                  {reportItems.map((item) => (
                    <NavItem key={item.label} item={item} isCollapsed={false} />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          // FINANCE WORKSPACE
          <>
            {/* Finance Start + Daily Overview */}
            <div className="space-y-1">
              <NavItem item={financeMainMenuNav} isCollapsed={false} />
              <NavItem item={financeDashboardNav} isCollapsed={false} />
            </div>

            {/* Daily Work Section */}
            <NavSectionHeader label="Daily Work" isCollapsed={false} />

            <div className="space-y-1">
              <NavItem
                item={financeTransactionsNav}
                isCollapsed={false}
              />
              <NavItem item={financeAllocationsNav} isCollapsed={false} />
              <NavItem item={financeCollectionsNav} isCollapsed={false} />
              <NavItem item={financePaymentsDueNav} isCollapsed={false} />
              <NavItem item={financeChequeNav} isCollapsed={false} />
              <NavItem item={financeActionItemsNav} isCollapsed={false} />
            </div>

            {/* Masters Section */}
            <NavSectionHeader label="Masters" isCollapsed={false} />

            <div className="space-y-1">
              <NavItem
                item={financeAccountsNav}
                isCollapsed={false}
                isExpanded={expandedSections.financeAccounts}
                onToggle={() => toggleSection('financeAccounts')}
              />
            </div>

            {/* Reports Section */}
            <NavSectionHeader label="Reports" isCollapsed={false} />

            <div className="space-y-1">
              <NavItem item={financeReportsNav} isCollapsed={false} />
            </div>
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t px-3 py-4" onClick={handleNavClick}>
        <div className="space-y-1">
          {workspace === 'finance' ? (
            <>
              <NavItem item={financeSettingsNav} isCollapsed={false} />
              <NavItem item={financeUserGuideNav} isCollapsed={false} />
            </>
          ) : workspace === 'sales' ? (
            <>
              <NavItem item={salesDashboardNav} isCollapsed={false} />
              <NavItem item={salesWebsiteEnquiriesNav} isCollapsed={false} />
              {menuAccess.customerList && <NavItem item={salesCompaniesNav} isCollapsed={false} />}
              {showBookOfBusiness && <NavItem item={salesBookOfBusinessNav} isCollapsed={false} />}
              <NavItem item={salesSettingsNav} isCollapsed={false} />
            </>
          ) : (
            footerItems.map((item) => (
              <NavItem key={item.label} item={item} isCollapsed={false} />
            ))
          )}
        </div>
      </div>
    </div>
  )
}
